import { useState } from 'react';

function SiblingTrackingAction({ order, secretKey, onNeedAuth, onAuthFailed, onApplied }) {
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(false);

  const isReplacement = Boolean(order.tracking) && order.tracking !== order.suggested_tracking;

  const handleApply = async () => {
    setApplying(true);
    setError(null);
    try {
      const res = await fetch(`/api/orders/${order.id}/apply-sibling-tracking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: secretKey,
          expected_tracking: order.suggested_tracking,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403) {
          onAuthFailed();
          setConfirming(false);
          return;
        }
        throw new Error(data.error || 'Failed to apply tracking');
      }
      setApplied(true);
      setConfirming(false);
      if (onApplied) onApplied();
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  if (applied) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 whitespace-nowrap">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        Tracking applied
      </span>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      {confirming ? (
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleApply}
            disabled={applying}
            className="px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors whitespace-nowrap"
          >
            {applying
              ? 'Applying...'
              : isReplacement
              ? `Replace with ${order.suggested_tracking}?`
              : `Confirm ${order.suggested_tracking}`}
          </button>
          <button
            onClick={() => { setConfirming(false); setError(null); }}
            className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => {
            if (!secretKey) {
              onNeedAuth(order.id);
              return;
            }
            setError(null);
            setConfirming(true);
          }}
          className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
        >
          {isReplacement ? 'Replace tracking' : 'Apply tracking'}
        </button>
      )}
      {error && <span className="text-xs text-red-500 text-right max-w-[16rem]">{error}</span>}
    </div>
  );
}

export default function AlertBanner({ flaggedOrders, secretKey, onNeedAuth, onAuthFailed, onOrderUpdated }) {
  const [expanded, setExpanded] = useState(false);

  if (!flaggedOrders || flaggedOrders.length === 0) {
    return (
      <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3.5 mb-6">
        <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-sm text-emerald-800 font-medium">All orders look good — no issues detected.</p>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl mb-6 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-amber-100/50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">{flaggedOrders.length}</span>
          </div>
          <p className="text-sm text-amber-900 font-semibold">
            {flaggedOrders.length} order{flaggedOrders.length !== 1 ? 's' : ''} need{flaggedOrders.length === 1 ? 's' : ''} attention
          </p>
        </div>
        <svg
          className={`w-4 h-4 text-amber-600 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-5 pb-4 space-y-2">
          {flaggedOrders.map((order) => (
            <div key={order.id} className="bg-white rounded-lg border border-amber-200/70 px-4 py-3 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900 font-mono">
                    {order.order_uid}
                  </span>
                  {order.batch_production ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-xs font-mono font-medium">
                      {order.batch_production}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300 font-medium">no batch</span>
                  )}
                  <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">{order.client}</span>
                </div>
                <div className="mt-1.5 space-y-0.5">
                  {order.flag_reasons.map((reason, i) => (
                    <p key={i} className="text-xs text-amber-700 leading-relaxed">{reason}</p>
                  ))}
                  {order.suggested_tracking && (
                    <p className="text-xs text-indigo-600 font-medium mt-1">
                      Suggested tracking: <span className="font-mono">{order.suggested_tracking}</span> (from {order.sibling_order_uid}
                      {order.sibling_batch_production && (
                        <>
                          {' · batch '}
                          <span className="font-mono">{order.sibling_batch_production}</span>
                        </>
                      )}
                      )
                    </p>
                  )}
                </div>
              </div>
              {order.suggested_tracking && (
                <div className="flex-shrink-0">
                  <SiblingTrackingAction
                    order={order}
                    secretKey={secretKey}
                    onNeedAuth={onNeedAuth}
                    onAuthFailed={onAuthFailed}
                    onApplied={onOrderUpdated}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
